import React from 'react';
import { Form, Select, Input, Button, Tooltip, Typography, ResourceGroupSelector } from '../../../../components';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { FormIntroduction } from '../components/FormIntroduction';
import { InstanceTagsLabel } from '../components/InstanceTagsLabel';
import { LoadingState } from '../components/LoadingState';
import { CloudAttributesError } from '../components/CloudAttributesError';
import { CommonApplianceConfiguration } from '../components/CommonApplianceConfiguration';
import { VMBasedMigrationsConfig } from '../components/VMBasedMigrationsConfig';
import { useAzureApplianceSetup } from './useAzureApplianceSetup';
import { AzureNetworkConfig } from './AzureNetworkConfig';
import { KeyValuePairs } from '../components/KeyValuePairs';

const { Title } = Typography;

export const AzureApplianceSetup = ({ cloudAccount, onSubmit, onCancel, submitting, isRedeploy }) => {
    const {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingAttributes,
        loadingRegionData,
        subscription,
        region,
        virtualNetwork,
        subnets,
        resourceGroups,
        instanceTags,
        setInstanceTags,
        handleSubscriptionChange,
        handleRegionChange,
        handleVirtualNetworkChange,
        handleFormSubmit,
        subscriptionOptions,
        regionOptions,
        virtualNetworkOptions,
        commonConfigProps,
    } = useAzureApplianceSetup({ cloudAccount, onSubmit, isRedeploy });

    if (initialLoading) {
        return <LoadingState />;
    }

    if (loadingError) {
        return (
            <div className="azure-appliance-setup">
                <FormIntroduction />
                <CloudAttributesError error={loadingError} onRetry={loadCloudAttributes} />
            </div>
        );
    }

    return (
        <div className="azure-appliance-setup">
            <FormIntroduction />
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                initialValues={{ assignPublicIp: 'none', staticPrivateIp: '', useProxyEnabled: true }}
            >
                <Title level={4}>Azure Account Setup</Title>

                <Form.Item
                    label="Subscription"
                    name="subscription"
                    rules={[{ required: true, message: 'Please select a subscription' }]}
                >
                    <Select
                        placeholder="Select a Subscription"
                        onChange={handleSubscriptionChange}
                        loading={loadingAttributes}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={subscriptionOptions}
                    />
                </Form.Item>

                <Form.Item label="Region" name="region" rules={[{ required: true, message: 'Please select a region' }]}>
                    <Select
                        placeholder="Select a Region"
                        onChange={handleRegionChange}
                        disabled={!subscription}
                        loading={loadingRegionData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={regionOptions}
                    />
                </Form.Item>

                {loadingRegionData && <div className="loading-indicator">Loading region data...</div>}

                <Form.Item
                    label="Resource Group"
                    name="resourceGroup"
                    rules={[{ required: true, message: 'Please enter or select a resource group' }]}
                >
                    <ResourceGroupSelector
                        resourceGroups={resourceGroups}
                        loading={loadingRegionData}
                        disabled={!region || loadingRegionData}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Size{' '}
                            <Tooltip title="This is the VM size used for the Migration Appliance. Prior to migration, you will be able to specify the VM size for your migration.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                >
                    <Select
                        disabled
                        defaultValue="Standard_B2s"
                        options={[{ value: 'Standard_B2s', label: 'Standard_B2s' }]}
                    />
                </Form.Item>

                <Form.Item label="Storage">
                    <span>8 GiB</span>
                </Form.Item>

                <Form.Item
                    label="Virtual Network"
                    name="virtualNetwork"
                    rules={[{ required: true, message: 'Please select a virtual network' }]}
                >
                    <Select
                        placeholder="Select a Virtual Network"
                        onChange={handleVirtualNetworkChange}
                        disabled={!region || loadingRegionData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={virtualNetworkOptions}
                    />
                </Form.Item>

                <AzureNetworkConfig networks={subnets} disabled={!virtualNetwork || loadingRegionData} />

                <Form.Item
                    label={<InstanceTagsLabel />}
                >
                    <KeyValuePairs
                        dataSource={instanceTags}
                        onChange={setInstanceTags}
                        keyPlaceholder="key"
                        valuePlaceholder="value"
                        instructionMessage="Click the + icon or press Enter to add a tag. You can add up to 50 tags."
                        limit={50}
                        keyValidation={{ maxLength: 127 }}
                        valueValidation={{ maxLength: 255 }}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            VM Name{' '}
                            <Tooltip title="VM Name of the Migration Appliance.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="vmName"
                    rules={[
                        { required: true, message: 'Please enter a VM name' },
                        { max: 64, message: 'VM name should not be more than 64 characters' },
                    ]}
                >
                    <Input placeholder="Enter a VM Name" />
                </Form.Item>

                <VMBasedMigrationsConfig />

                <CommonApplianceConfiguration {...commonConfigProps} />

                <div className="modal-footer">
                    <Button type="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={submitting}>
                        Initiate Migration Appliance checks
                    </Button>
                </div>
            </Form>
        </div>
    );
};
